import type { ApiClientConfiguration } from '@/shared/types/api';

// --- Types & Enums ---
export type { LogLevel } from '@/infra/logging';

enum Environment {
  Development = 'development',
  Staging = 'staging',
  Production = 'production',
}

export interface AppSettings {
  api: ApiClientConfiguration;
  app: {
    name: string;
    environment: Environment;
    isDevelopment: boolean;
  };
  logging: {
    enabled: boolean;
    logsEndpoint: string;
  };
}

// --- Global Window Type ---
declare global {
  interface Window {
    __ENV?: {
      APP_API_BASE_URL?: string;
      APP_ENV?: string;
    };
  }
}

// --- Internal Helpers ---
function getEnv(key: string): string | undefined {
  return typeof window !== 'undefined' && window.__ENV
    ? window.__ENV[key as keyof typeof window.__ENV]
    : undefined;
}

function validateAppEnv(value: string | undefined): Environment {
  const env = value ?? Environment.Development;
  if (!Object.values(Environment).includes(env as Environment)) {
    throw new Error(
      `Invalid APP_ENV value: "${env}"\nMust be one of: ${Object.values(Environment).join(', ')}`
    );
  }
  return env as Environment;
}

// --- Factory & Singleton ---
function createAppSettings(): AppSettings {
  const apiBaseUrl = getEnv('APP_API_BASE_URL') ?? '/api';
  const appEnv = validateAppEnv(getEnv('APP_ENV'));
  const isDevelopment = appEnv === Environment.Development;

  const appSettings: AppSettings = {
    api: {
      baseUrl: apiBaseUrl,
      timeout: 10000,
      retry: {
        enabled: false,
        maxRetries: 3,
        baseDelayMs: 1000,
        maxDelayMs: 10000,
        retryableStatuses: [500, 502, 503, 504],
        retryableCodes: ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'],
      },
    },
    app: {
      name: 'Craig Admin',
      environment: appEnv,
      isDevelopment,
    },
    logging: {
      enabled: appEnv === Environment.Production,
      logsEndpoint: '/loki/logs',
    },
  };

  if (isDevelopment) {
    console.log('App Settings:', {
      environment: appEnv,
      apiBaseUrl,
    });
  }

  return appSettings;
}

// --- Export Singleton ---
const appSettings: AppSettings = createAppSettings();
export { appSettings };
export default appSettings;
